import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, BarChart3, UserCheck } from 'lucide-react';
import clsx from 'clsx';
import { User, ROLE_GROUPS, ROLES } from '@app-types';
import { hasRole } from '@utils/permissionUtils';
import { usePermissions } from '@contexts/AuthContext';
import styles from './QuickActions.module.css';

interface QuickActionsProps {
  user: User;
}

const QuickActions: React.FC<QuickActionsProps> = ({ user }) => {
  const navigate = useNavigate();
  const permissions = usePermissions();

  const getActionsForRole = () => {
    type ActionItem = {
      label: string;
      icon: React.ComponentType<{ size?: number }>;
      action: () => void;
      variant: 'primary' | 'secondary';
      description: string;
    };

    const baseActions: ActionItem[] = [];

    // Only show assessments if user has permission
    if (permissions.canAccessAssessments) {
      baseActions.push({
        label: 'View All Assessments',
        icon: FileText,
        action: () => { navigate('/assessments'); },
        variant: 'secondary',
        description: 'Browse all assessments in the system'
      });
    }

    // Multi-role: Check each role and combine actions (Admin actions take priority)
    const actions: ActionItem[] = [];

    if (hasRole(user, ROLE_GROUPS.FULL_ACCESS)) {
      actions.push({
        label: 'System Reports',
        icon: BarChart3,
        action: () => { navigate('/reports'); },
        variant: 'secondary',
        description: 'Generate comprehensive reports'
      });
    } else if (hasRole(user, ROLES.CONTRACT_ROLE)) {
      actions.push(
        {
          label: 'Upload Evidence',
          icon: UserCheck,
          action: () => { navigate('/evidence'); },
          variant: 'secondary',
          description: 'Upload evidence for assigned contracts'
        },
        {
          label: 'My Contracts',
          icon: BarChart3,
          action: () => { navigate('/contracts'); },
          variant: 'secondary',
          description: 'View contracts assigned to you'
        }
      );
    }

    if (hasRole(user, ROLES.ASSESSOR)) {
      actions.push(
        {
          label: 'My Assignments',
          icon: UserCheck,
          action: () => { navigate(`/assessments?assessor_id=${user.id}`); },
          variant: 'primary',
          description: 'View assessments assigned to you'
        },
        {
          label: 'Pending Evaluations',
          icon: FileText,
          action: () => { navigate(`/assessments?assessor_id=${user.id}&status=Pending`); },
          variant: 'secondary',
          description: 'Continue incomplete evaluations'
        }
      );
    }

    return [...actions, ...baseActions];
  };

  const actions = getActionsForRole();

  return (
    <div className={styles.quickActionsContainer}>
      <div className={styles.actionsHeader}>
        <h3>Quick Actions</h3>
        <p>Common tasks for {user.roles?.join(', ') || 'Contact'}</p>
      </div>

      <div className={styles.actionsGrid}>
        {actions.map((action) => {
          const IconComponent = action.icon;
          return (
            <button
              type="button"
              key={`action-${action.label}`}
              className={clsx(
                styles.actionCard,
                action.variant === 'primary' && styles.actionCardPrimary,
                action.variant === 'secondary' && styles.actionCardSecondary
              )}
              onClick={action.action}
            >
              <div className={styles.actionContent}>
                <div className={styles.actionIcon}>
                  <IconComponent size={20} />
                </div>
                <div className={styles.actionDetails}>
                  <div className={styles.actionLabel}>{action.label}</div>
                  <div className={styles.actionDescription}>{action.description}</div>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default QuickActions;
